import React from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import AddIcon from '@/assets/icons/add';
import CircleMinus from '@/assets/icons/circle-minus';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { ProjectionType } from '@/types/api.generated';
import { getColorByProjType } from '@/utils/getProjectionsTypes';

import { checkIfSpecialOrFreePickProjection } from '../../utils/lineupsUtils';

type ProjectionDescriptionTileProps = {
    selectedProjection: PlayerProjectionFragment;
    isSuspended?: boolean;
    handleToggleProjection: (direction: 'prev' | 'next') => void;
    hasNextProjection: boolean;
    hasPreviousProjection: boolean;
};

export const ProjectionDescriptionTile = ({
    selectedProjection,
    isSuspended,
    handleToggleProjection,
    hasNextProjection,
    hasPreviousProjection,
}: ProjectionDescriptionTileProps) => {
    const selectedProjectionValue =
        selectedProjection?.type === ProjectionType.Regular
            ? selectedProjection?.value
            : selectedProjection?.nonRegularValue;

    const specialIncrease =
        (selectedProjection?.nonRegularPercentage && selectedProjection?.nonRegularPercentage > 0) || false;

    const textColor = selectedProjection?.type
        ? getColorByProjType(selectedProjection.type, specialIncrease)
        : ds.colorToken.text.primary;
    const isSpecialOrFreeProjection = checkIfSpecialOrFreePickProjection(selectedProjection?.type);
    const shoulShowCrossedOutValue =
        isSpecialOrFreeProjection && !!selectedProjection.value && selectedProjection.value > 0;

    return (
        <Box flex={1} alignItems={'center'}>
            {!isSuspended ? (
                <Row alignItems={'center'} width={'100%'} justifyContent="center">
                    {hasPreviousProjection ? (
                        <TouchableOpacity
                            testID="player-tile-projection-minus"
                            onPress={() => handleToggleProjection('prev')}
                            hitSlop={12}
                        >
                            <CircleMinus />
                        </TouchableOpacity>
                    ) : (
                        <Box width={24} height={24} />
                    )}

                    <Row justifyContent="center" alignItems="center" minWidth={80}>
                        {shoulShowCrossedOutValue ? (
                            <>
                                <Text
                                    variant="bodyMedium"
                                    lineHeight={20}
                                    textDecorationLine={'line-through'}
                                    color={'neutral400'}
                                    letterSpacing={-0.23}
                                    style={styles.crossedOutValue}
                                >
                                    {selectedProjection.value}
                                </Text>
                                <SizedBox value={4} />
                            </>
                        ) : null}
                        <AnimatedNumber
                            alignText={'flex-start'}
                            variant="headlineSmall"
                            decimals={1}
                            value={selectedProjectionValue ?? 0}
                            color={textColor}
                            fontWeight={700}
                            letterSpacing={-0.45}
                        />
                    </Row>
                    {hasNextProjection ? (
                        <TouchableOpacity
                            testID="player-tile-projection-plus"
                            onPress={() => handleToggleProjection('next')}
                            hitSlop={12}
                        >
                            <AddIcon />
                        </TouchableOpacity>
                    ) : (
                        <Box width={24} height={24} />
                    )}
                </Row>
            ) : (
                <Text variant="headlineSmall" fontWeight={700} color={ds.colorToken.text.secondary}>
                    --
                </Text>
            )}
            <Text color={'neutral300'} variant="bodySmall" style={styles.projectionLabelSpacing} numberOfLines={2}>
                {selectedProjection?.label}
            </Text>
        </Box>
    );
};

const styles = StyleSheet.create({
    projectionLabelSpacing: {
        marginTop: -2,
    },
    crossedOutValue: {
        transform: [{ translateY: 1 }],
    },
});
